/* ==========================================================================
   Solid Viibe — AJAX form submission and validation

   Progressive enhancement only. Every form handled here posts normally when
   JavaScript is unavailable, and nothing below removes or rewrites the action,
   method, name or hidden inputs Shopify emits.

   Opt in with data-ajax-form on the form. An optional
   [data-form-status] element receives the form-level message; one is created
   if the form does not supply it.
   ========================================================================== */
(function () {
  'use strict';

  /* Two opt-ins, not one. data-ajax-form posts in the background and needs
     the field checking that comes with it; data-validate-form wants only the
     checking, for a form that has to submit for real — Shopify's comment
     form, whose CAPTCHA token only counts on a genuine browser submission.

     They were the same attribute until the review form dropped ajax and
     silently lost every required-field message with it. */
  var forms = document.querySelectorAll('[data-ajax-form], [data-validate-form]');
  if (!forms.length) return;

  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var uid = 0;

  /* --- Field-level messages -------------------------------------------- */

  /* The message goes inside the field's own wrapper, under the input, and
     only falls back to "next sibling of the field" where there is no wrapper
     (the footer sign-up's flat markup — searching with closest() there walked
     past it and appended to the bottom of the whole footer column).

     It has to go inside rather than after: .svct-fields--2 is a two-column
     grid, so a message inserted as a sibling of the wrapper became a grid
     item of its own and sat in the next column beside the input instead of
     beneath it. */
  function wrapperFor(field) {
    var wrap = field.closest('.sv-field, .svct-field, .svw-field');
    return wrap && wrap.contains(field) ? wrap : null;
  }

  function fieldMessage(field, text) {
    var wrap = wrapperFor(field);
    var scope = wrap || field.parentNode;
    var msg = scope.querySelector('[data-field-error][data-for="' + field.name + '"]');

    if (!text) {
      if (msg) msg.remove();
      field.removeAttribute('aria-invalid');
      field.classList.remove('is-invalid');
      var described = field.getAttribute('aria-describedby');
      if (described && described.indexOf('fe-') === 0) field.removeAttribute('aria-describedby');
      return;
    }

    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'sv-field-error';
      msg.setAttribute('data-field-error', '');
      msg.setAttribute('data-for', field.name || '');
      uid += 1;
      msg.id = 'fe-' + uid;
      if (wrap) {
        wrap.appendChild(msg);
      } else {
        field.parentNode.insertBefore(msg, field.nextSibling);
      }
    }
    msg.textContent = text;
    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', msg.id);
    field.classList.add('is-invalid');
  }

  function labelFor(field) {
    var lab = field.labels && field.labels[0];
    var text = lab ? lab.textContent : (field.getAttribute('placeholder') || 'This field');
    return text.replace(/\(optional\)/i, '').replace(/\s+/g, ' ').trim().replace(/[:*]$/, '');
  }

  function problem(field) {
    var value = (field.value || '').trim();
    if (field.hasAttribute('required') && !value) return labelFor(field) + ' is required.';
    if (value && field.type === 'email' && !EMAIL.test(value)) return 'Enter a valid email address.';
    /* Let the browser's own constraints (pattern, minlength, min/max) speak
       for anything this does not cover. */
    if (typeof field.checkValidity === 'function' && !field.checkValidity()) {
      return field.validationMessage || 'Check this field.';
    }
    return '';
  }

  function validate(form) {
    var fields = form.querySelectorAll('input, select, textarea');
    var first = null;
    Array.prototype.forEach.call(fields, function (field) {
      if (field.type === 'hidden' || field.disabled) return;
      var text = problem(field);
      fieldMessage(field, text);
      if (text && !first) first = field;
    });
    return first;
  }

  /* --- Form-level status ------------------------------------------------ */

  function statusBox(form) {
    var box = form.querySelector('[data-form-status]');
    if (!box) {
      box = document.createElement('div');
      box.setAttribute('data-form-status', '');
      form.insertBefore(box, form.firstChild);
    }
    box.className = 'sv-form-status';
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    return box;
  }

  function say(form, kind, text) {
    var box = statusBox(form);
    box.className = 'sv-form-status sv-form-status--' + kind;
    box.textContent = text;
  }

  /* --- Reading Shopify's own answer out of the response -----------------
     Shopify replies with a whole rendered page. The reliable signals are the
     error list it prints for the form and the theme's own posted_successfully
     block, so both are read from the returned document rather than guessed
     at from the status code. */

  function readResponse(html, form) {
    var doc = new DOMParser().parseFromString(html, 'text/html');

    var errorNode = doc.querySelector('.errors, #error_message, .form-status--error, [data-form-error]');
    if (errorNode && errorNode.textContent.trim()) {
      return { ok: false, text: errorNode.textContent.trim().replace(/\s+/g, ' ') };
    }

    /* The theme renders its success copy inside the form, so look for a
       role="status" that came back in the same kind of form. */
    var formId = form.getAttribute('id');
    var scope = (formId && doc.getElementById(formId)) || doc;
    var okNode = scope.querySelector('[role="status"]');
    if (okNode && okNode.textContent.trim()) {
      return { ok: true, text: okNode.textContent.trim().replace(/\s+/g, ' ') };
    }

    return { ok: true, text: '' };
  }

  /* --- Submit ----------------------------------------------------------- */

  function busy(form, on) {
    var button = form.querySelector('[type="submit"]');
    if (button) button.disabled = on;
    if (on) form.setAttribute('aria-busy', 'true');
    else form.removeAttribute('aria-busy');
  }

  Array.prototype.forEach.call(forms, function (form) {
    /* Clear a field's error as soon as it becomes valid, so the form stops
       shouting at someone who is already fixing it. */
    form.addEventListener('input', function (e) {
      var field = e.target;
      if (!field.matches || !field.matches('input, select, textarea')) return;
      if (field.getAttribute('aria-invalid') !== 'true') return;
      if (!problem(field)) fieldMessage(field, '');
    });

    form.addEventListener('submit', function (e) {
      var firstBad = validate(form);

      if (firstBad) {
        e.preventDefault();
        say(form, 'error', 'Check the highlighted fields and try again.');
        firstBad.focus();
        return;
      }

      /* Validation passed. A form that only asked for checking now submits
         the ordinary way, which is the whole point of it opting out. */
      if (!form.hasAttribute('data-ajax-form')) return;

      if (!window.fetch || !window.FormData || !window.DOMParser) return; /* post normally */

      e.preventDefault();
      busy(form, true);
      say(form, 'pending', 'Sending…');

      var keepValues = form.hasAttribute('data-keep-values');

      fetch(form.action, {
        method: (form.method || 'POST').toUpperCase(),
        body: new FormData(form),
        headers: { Accept: 'text/html' }
      })
        .then(function (res) {
          /* Shopify answers a comment or contact post with its own anti-bot
             challenge whenever the traffic looks automated. The body that
             comes back carries no error node and no status, so readResponse
             read it as a plain success and we told someone their message had
             been sent when it had not. Nothing here may answer a challenge on
             a visitor's behalf, so hand them the page and let them do it. */
          if (res.redirected && res.url.indexOf('/challenge') !== -1) {
            window.location.href = res.url;
            return null;
          }
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          if (html === null) return; /* leaving for the challenge */
          var result = readResponse(html, form);
          if (result.ok) {
            say(form, 'success', result.text || 'Thanks — that came through.');
            if (!keepValues) form.reset();
          } else {
            say(form, 'error', result.text);
          }
        })
        .catch(function () {
          say(form, 'error', 'That could not be sent just now. Please try again, or email us directly.');
        })
        .then(function () { busy(form, false); });
    });
  });
})();
